// Ask Siggy chat surface. Renders the conversation as distinct
// user / Siggy bubbles, with markdown formatting on Siggy's replies.
// On first open, shows the PHI warning gate plus suggested starter
// questions. On send: POST /api/chat, append both turns, auto-scroll.
// Playbook reference: Section 5.11.

import { TextField } from '@shopify/polaris';
import { useEffect, useRef, useState } from 'react';
import { type ChatReply, usePostChat } from '../api/chat';
import { ChatMarkdown } from '../components/ChatMarkdown';
import { ComplianceNotice } from '../components/ComplianceNotice';
import { PreChatPhiWarning } from '../components/PreChatPhiWarning';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { SeniorBanner } from '../components/senior/SeniorBanner';
import { SeniorButton } from '../components/senior/SeniorButton';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { useTts } from '../hooks/useTts';
import { AppPage } from '../layouts/AppPage';

interface ChatTurn {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  escalated?: boolean;
}

const STARTER_QUESTIONS = [
  'How does Tylenol work, and what is the max dose for an adult?',
  'What are the most common side effects of lisinopril?',
  'I take metformin. Should I take it with food?',
  'What is the difference between ibuprofen and acetaminophen?',
];

const FOLLOWUPS = [
  'What are the warning signs to watch for?',
  'What if I miss a dose?',
  'Are there foods I should avoid?',
  'How does this interact with other medicines?',
];

export function Chat() {
  const postChat = usePostChat();
  const [acknowledged, setAcknowledged] = useState(false);
  const [draft, setDraft] = useState('');
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to the latest message whenever the conversation grows.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  async function sendMessage(text: string) {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setError(null);
    setSending(true);
    const userTurnId = `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setTurns((prev) => [...prev, { id: userTurnId, role: 'user', text: trimmed }]);
    setDraft('');
    try {
      const reply: ChatReply = await postChat(trimmed, conversationId ?? undefined);
      if (reply.conversationId) setConversationId(reply.conversationId);
      const assistantTurnId =
        reply.messageId ?? `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setTurns((prev) => [
        ...prev,
        {
          id: assistantTurnId,
          role: 'assistant',
          text: reply.reply,
          escalated: reply.escalation.triggered,
        },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  }

  const onSendClick = () => {
    void sendMessage(draft);
  };

  const onStarter = (q: string) => {
    void sendMessage(q);
  };

  const canSend = acknowledged && !sending && draft.trim().length > 0;

  return (
    <AppPage title="Ask Siggy" back={{ to: '/medications', label: 'Medications' }}>
      <ComplianceNotice variant="ai" />
      {!acknowledged ? <PreChatPhiWarning onAcknowledge={() => setAcknowledged(true)} /> : null}

      <div className="chat-thread" aria-live="polite">
        {turns.length === 0 ? (
          <EmptyState onPick={onStarter} disabled={!acknowledged || sending} />
        ) : (
          turns.map((t, idx) => (
            <ChatBubble
              key={t.id}
              turn={t}
              showFollowups={
                t.role === 'assistant' && idx === turns.length - 1 && !sending && !t.escalated
              }
              onFollowup={onStarter}
            />
          ))
        )}
        {sending ? <TypingIndicator /> : null}
        <div ref={endRef} />
      </div>

      {error ? (
        <SeniorBanner title="Could not send your message" tone="critical">
          {error}
        </SeniorBanner>
      ) : null}

      <div className="chat-composer">
        <TextField
          label="Your question for Siggy"
          labelHidden
          value={draft}
          onChange={setDraft}
          multiline={3}
          placeholder="Ask Siggy anything about your medicines"
          autoComplete="off"
          disabled={!acknowledged || sending}
        />
        <div className="chat-composer__actions">
          <MicButton onTranscript={setDraft} disabled={!acknowledged || sending} />
          <SeniorButton
            onClick={onSendClick}
            variant="primary"
            fullWidth
            disabled={!canSend}
            data-testid="send-chat"
          >
            {sending ? 'Siggy is thinking...' : 'Send'}
          </SeniorButton>
        </div>
      </div>
    </AppPage>
  );
}

function EmptyState({
  onPick,
  disabled,
}: {
  onPick: (q: string) => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <div className="chat-empty">
      <h2 className="chat-empty__title">What can I help you with?</h2>
      <p className="chat-empty__lead">
        Ask me anything about your medicines. I can explain how a medicine works, what the label
        says, common side effects, and what to watch out for. For decisions about your own
        prescriptions, your pharmacist and doctor are still the experts.
      </p>
      <p className="chat-empty__hint">Try one of these to get started:</p>
      <ul className="chat-starters">
        {STARTER_QUESTIONS.map((q) => (
          <li key={q}>
            <button
              type="button"
              className="chat-starter"
              onClick={() => onPick(q)}
              disabled={disabled}
            >
              {q}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChatBubble({
  turn,
  showFollowups,
  onFollowup,
}: {
  turn: ChatTurn;
  showFollowups?: boolean;
  onFollowup?: (q: string) => void;
}): JSX.Element {
  const isUser = turn.role === 'user';
  return (
    <>
      <div className={isUser ? 'chat-row chat-row--user' : 'chat-row chat-row--siggy'}>
        {!isUser ? (
          <span className="chat-siggy-avatar">
            <SiggyAvatar size={40} />
          </span>
        ) : null}
        <div className="chat-bubble">
          <div className="chat-bubble__header">
            <span className="chat-bubble__author">{isUser ? 'You' : 'Siggy'}</span>
            {!isUser ? <ListenButton messageId={turn.id} text={turn.text} /> : null}
          </div>
          {turn.escalated ? (
            <div className="chat-bubble__escalation">
              <strong>Please ask the real Dr. Marlee.</strong> This question touches on a medication
              that needs a real pharmacist to weigh in for your situation. The Premium consult
              option is in your account.
            </div>
          ) : null}
          {isUser ? (
            <p className="chat-bubble__text">{turn.text}</p>
          ) : (
            <ChatMarkdown text={turn.text} />
          )}
        </div>
      </div>
      {showFollowups && onFollowup ? (
        <div className="followups" aria-label="Suggested follow-up questions">
          {FOLLOWUPS.map((q) => (
            <button key={q} type="button" className="followup-chip" onClick={() => onFollowup(q)}>
              {q}
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}

function ListenButton({
  messageId,
  text,
}: { messageId: string; text: string }): JSX.Element | null {
  const tts = useTts(messageId, text);
  if (!tts.supported) return null;
  const isBusy = tts.state === 'loading';
  const isPlaying = tts.state === 'playing';
  const label = isPlaying ? 'Stop' : isBusy ? 'Loading...' : 'Listen';
  return (
    <button
      type="button"
      className={`listen-btn${isPlaying ? ' is-playing' : ''}`}
      onClick={() => {
        void tts.play();
      }}
      disabled={isBusy}
      aria-label={isPlaying ? 'Stop listening' : 'Listen to Siggy'}
      title={label}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20">
        <title>{label}</title>
        {isPlaying ? (
          <>
            <rect x="6" y="5" width="4" height="14" fill="currentColor" rx="1" />
            <rect x="14" y="5" width="4" height="14" fill="currentColor" rx="1" />
          </>
        ) : (
          <>
            <path d="M12 3a4 4 0 0 0-4 4v5a4 4 0 0 0 8 0V7a4 4 0 0 0-4-4z" fill="currentColor" />
            <path
              d="M5 11v1a7 7 0 0 0 14 0v-1M12 19v3M9 22h6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </>
        )}
      </svg>
      <span className="listen-btn__label">{label}</span>
    </button>
  );
}

function MicButton({
  onTranscript,
  disabled,
}: {
  onTranscript: (text: string) => void;
  disabled: boolean;
}): JSX.Element | null {
  const speech = useSpeechRecognition();
  const baseline = useRef('');
  if (!speech.supported) return null;
  const listening = speech.state === 'listening';
  const onClick = () => {
    if (listening) {
      speech.stop();
      return;
    }
    baseline.current = '';
    speech.start((text, isFinal) => {
      // Replace the live transcript region after the baseline so interim
      // results don't pile up. On final, persist into the baseline.
      const prefix = baseline.current ? `${baseline.current.trimEnd()} ` : '';
      const next = `${prefix}${text}`;
      onTranscript(next);
      if (isFinal) {
        baseline.current = next;
      }
    });
  };
  return (
    <button
      type="button"
      className={`mic-btn${listening ? ' is-listening' : ''}`}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={listening}
      aria-label={listening ? 'Stop listening' : 'Speak your question'}
      title={listening ? 'Stop' : 'Speak'}
    >
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
        <title>{listening ? 'Stop' : 'Speak'}</title>
        <path d="M12 3a4 4 0 0 0-4 4v5a4 4 0 0 0 8 0V7a4 4 0 0 0-4-4z" fill="currentColor" />
        <path
          d="M5 11v1a7 7 0 0 0 14 0v-1M12 19v3M9 22h6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

function TypingIndicator(): JSX.Element {
  return (
    <div className="chat-row chat-row--siggy" aria-label="Siggy is typing">
      <span className="chat-siggy-avatar">
        <SiggyAvatar size={40} />
      </span>
      <div className="chat-bubble chat-bubble--typing">
        <div className="chat-bubble__author">Siggy</div>
        <div className="chat-typing">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}
